import { useQuery } from "@tanstack/react-query";
import { ExternalLink } from "lucide-react";

interface SponsorData {
  name: string | null;
  logoUrl: string | null;
  websiteUrl: string | null;
  active: boolean;
}

async function fetchSponsor(): Promise<SponsorData> {
  const res = await fetch("/api/sponsor");
  if (!res.ok) throw new Error("Failed");
  return res.json();
}

export function SponsorBanner() {
  const { data } = useQuery<SponsorData>({
    queryKey: ["sponsor"],
    queryFn: fetchSponsor,
    staleTime: 5 * 60 * 1000,
  });

  if (!data?.active || !data.name) return null;

  const inner = (
    <div className="flex items-center gap-3">
      {data.logoUrl && (
        <img
          src={data.logoUrl}
          alt={data.name}
          className="h-6 w-auto max-w-[100px] object-contain"
          onError={e => (e.currentTarget.style.display = "none")}
        />
      )}
      <div className="min-w-0">
        <p className="text-[9px] font-semibold text-slate-400 uppercase tracking-widest leading-none mb-0.5">Sponsored by</p>
        <p className="text-sm font-semibold text-slate-700 truncate">{data.name}</p>
      </div>
      {data.websiteUrl && <ExternalLink size={13} className="text-slate-400 shrink-0" />}
    </div>
  );

  const className = "flex items-center bg-white border border-slate-100 shadow-sm rounded-xl px-4 py-2.5 transition-opacity hover:opacity-80";

  if (data.websiteUrl) {
    return (
      <a href={data.websiteUrl} target="_blank" rel="noopener noreferrer" className={className}>
        {inner}
      </a>
    );
  }

  return <div className={className}>{inner}</div>;
}
